import React, { useState } from "react";
import Swal from "sweetalert2";
import {
  Avatar,
  Button,
  TextField,
  Link,
  Grid,
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Tabs,
  Tab,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useForm } from "react-hook-form";
import { useUserSignIn, useUserSignUp } from "../services/authService";
import { useNavigate } from "react-router-dom";

export default function AuthPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  const { mutate: signUpCall } = useUserSignUp();
  const { mutate: signInCall, data: signInData } = useUserSignIn();

  // react-hook-form instances
  const {
    register: registerSignIn,
    handleSubmit: handleSubmitSignIn,
    formState: { errors: errorsSignIn },
    reset: resetSignIn,
  } = useForm();

  const {
    register: registerSignUp,
    handleSubmit: handleSubmitSignUp,
    formState: { errors: errorsSignUp },
    reset: resetSignUp,
  } = useForm();

  const handleChange = (event, newValue) => {
    setTab(newValue);
  };

  // Forgot Password Modal
  const handleForgotPassword = (e) => {
    e.preventDefault();
    Swal.fire({
      title: "Need help resetting your password?",
      html: `<p style="font-size:16px;">Please contact us at <a href="mailto:contactus@atttic.com">contactus@atttic.com</a> for assistance.</p>`,
      icon: "info",
      showCloseButton: true,
      confirmButtonText: "Close",
      confirmButtonColor: "#0a3d62",
      width: 400,
      background: "#fff",
    });
  };

  // Submit handlers
  const onSignInSubmit = (data) => {
    signInCall(data, {
      onSuccess: (res) => {
        if (res?.token) {
          sessionStorage.setItem("authToken", res.token);
          navigate("/dashboard");
        }
      },
    });
  };

  const onSignUpSubmit = (data) => {
    const payload = {
      ...data,
      user_id: "10055",
      role: "admin",
    };

    signUpCall(payload, {
      onSuccess: () => {
        resetSignUp();
        setTab(0);
      },
    });
  };

  return (
    <Grid
      container
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0a3d62, #000000)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Container maxWidth="xs">
        <Card
          sx={{
            width: "100%",
            borderRadius: 3,
            boxShadow: 6,
            color: "white",
            textAlign: "center",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              bgcolor: "#0a3d62",
              color: "#ffffffff",
              py: 2,
            }}
          >
            <Avatar
              sx={{
                bgcolor: "black",
                color: "white",
                margin: "0 auto",
              }}
            >
              <LockOutlinedIcon />
            </Avatar>
            <Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }}>
              Welcome
            </Typography>
          </Box>

          {/* Tabs */}
          <Tabs
            value={tab}
            onChange={handleChange}
            variant="fullWidth"
            textColor="inherit"
            indicatorColor="secondary"
            sx={{
              "& .MuiTab-root": {
                color: "#000000ff",
                fontWeight: "bold",
              },
              "& .Mui-selected": {
                color: "#000000ff !important",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#0a3d62",
              },
            }}
          >
            <Tab label="Sign In" />
            <Tab label="Sign Up" />
          </Tabs>

          {/* Content */}
          <CardContent>
            {/* Sign In Form */}
            {tab === 0 && (
              <Box
                component="form"
                onSubmit={handleSubmitSignIn(onSignInSubmit)}
                noValidate
                sx={{ mt: 1 }}
              >
                <TextField
                  margin="normal"
                  fullWidth
                  label="Email Address"
                  autoComplete="email"
                  autoFocus
                  {...registerSignIn("email", {
                    required: "Email is required",
                  })}
                  error={!!errorsSignIn.email}
                  helperText={errorsSignIn.email?.message}
                />
                <TextField
                  margin="normal"
                  fullWidth
                  label="Password"
                  type="password"
                  autoComplete="current-password"
                  {...registerSignIn("password", {
                    required: "Password is required",
                  })}
                  error={!!errorsSignIn.password}
                  helperText={errorsSignIn.password?.message}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 3,
                    mb: 2,
                    bgcolor: "#0a3d62",
                    color: "white",
                    fontWeight: "bold",
                  }}
                >
                  Sign In
                </Button>
                <Grid>
                  <Box
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Link
                        href="#"
                        variant="body2"
                        sx={{ color: "#0a3d62", cursor: "pointer" }}
                        onClick={handleForgotPassword}
                      >
                        Forgot password?
                      </Link>
                    </Box>
                    <Box>
                      <Link
                        component={RouterLink}
                        to="/getting"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                        sx={{ color: "#0a3d62", cursor: "pointer" }}
                      >
                        Getting Started
                      </Link>
                    </Box>
                  </Box>
                </Grid>
              </Box>
            )}

            {/* Sign Up Form */}
            {tab === 1 && (
              <Box
                component="form"
                onSubmit={handleSubmitSignUp(onSignUpSubmit)}
                noValidate
                sx={{ mt: 1 }}
              >
                <TextField
                  margin="normal"
                  fullWidth
                  label="Full Name"
                  autoComplete="name"
                  {...registerSignUp("user_name", {
                    required: "Full name is required",
                  })}
                  error={!!errorsSignUp.user_name}
                  helperText={errorsSignUp.user_name?.message}
                />
                <TextField
                  margin="normal"
                  fullWidth
                  label="Email Address"
                  autoComplete="email"
                  {...registerSignUp("email", {
                    required: "Email is required",
                  })}
                  error={!!errorsSignUp.email}
                  helperText={errorsSignUp.email?.message}
                />
                <TextField
                  margin="normal"
                  fullWidth
                  label="Password"
                  type="password"
                  autoComplete="new-password"
                  {...registerSignUp("password", {
                    required: "Password is required",
                    minLength: {
                      value: 6,
                      message: "Password must be at least 6 characters",
                    },
                  })}
                  error={!!errorsSignUp.password}
                  helperText={errorsSignUp.password?.message}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 3,
                    mb: 2,
                    bgcolor: "#0a3d62",
                    color: "white",
                    fontWeight: "bold",
                  }}
                >
                  Sign Up
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </Grid>
  );
}
